import { useEffect, useMemo, useState } from "react";

import clsx from "clsx";
import { useAtom } from "jotai";
import { difference, orderBy, uniq } from "lodash";
import moment from "moment";
import { useLocalStorage } from "usehooks-ts";

import { useUser } from "@components/user-context";
import { SecondaryIconButton } from "@components/v2/buttons";
import { Select, TextInput } from "@components/v2/inputs";
import { ModalPanel } from "@components/v2/modal-panel";
import { Spinner } from "@components/v2/spinner";
import {
  createColumnHelper,
  HeaderWithInfoIcon,
  Table,
} from "@components/v2/table";
import { GenericTooltip } from "@components/v2/tooltips/tooltip-container";
import { useFeatureFlags, useUpdateUserFeatureFlag } from "@hooks/http";
import { Switch } from "@mantine/core";
import { Modal } from "@shared/modal";
import { Radio } from "@shared/radio-group";

import { queryMetadataAtom, adminModalIsOpenAtom } from "../atoms";
import { type QueryMetadata } from "../types";

import { CellTextFormatter } from "./cell-text-formatter";
import { Panel } from "./panel";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export const useAdminModalIsOpen = () => {
  return useAtom(adminModalIsOpenAtom);
};

export const AdminModal = (props: Props) => {
  const { user } = useUser();

  if (!user.isAdmin) {
    return null;
  }
  return (
    <Modal
      contentClassName="flex flex-col"
      bodyClassName="flex flex-col overflow-hidden"
      variant="v2"
      {...props}
    >
      <Content />
    </Modal>
  );
};

const Content = () => {
  const [queryMetadata, setQueryMetadata] = useAtom(queryMetadataAtom);
  const [updateFeatureFlag, { loading: updateFeatureFlagLoading }] =
    useUpdateUserFeatureFlag();

  const { hasFeatureFlag } = useFeatureFlags();
  const [selection, setSelection] = useState<Record<string, boolean>>({});
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 25 });
  const [selectedContext, setSelectedContext] = useState<string | null>(null);
  const [selectedTopic, setSelectedTopic] = useState<string>("");

  const [sorting, setSorting] = useLocalStorage<
    { id: string; desc: boolean }[]
  >("admin-modal-v2-sorting", [{ id: "execution-timestamp", desc: true }]);

  const [columnWidths, setColumnWidths] = useLocalStorage<
    Record<string, number>
  >("admin-modal-v2-column-widths", {
    context: 140,
    topic: 280,
    "execution-timestamp": 120,
    "job-id": 340,
    "query-id": 100,
  });

  const selectedRow = useMemo(() => {
    return queryMetadata.find((e) => Object.keys(selection).includes(e.id));
  }, [queryMetadata, selection]);

  const filteredQueryMetadata = useMemo(() => {
    return queryMetadata.filter(
      (e) =>
        (selectedContext ? e.context === selectedContext : true) &&
        (selectedTopic
          ? e.topic.toLowerCase().includes(selectedTopic.toLowerCase())
          : true),
    );
  }, [queryMetadata, selectedContext, selectedTopic]);

  const sortedQueryMetadata = useMemo(() => {
    const property =
      {
        context: "context",
        topic: "topic",
        "execution-timestamp": "log.executionTimestamp",
        "job-id": "log.jobId",
        "query-id": "log.queryId",
      }[sorting[0]?.id] ?? "log.executionTimestamp";

    return orderBy(
      filteredQueryMetadata,
      [property],
      [sorting[0] && sorting[0].desc ? "desc" : "asc"],
    );
  }, [filteredQueryMetadata, sorting]);

  const pagedQueryMetadata = useMemo(() => {
    return sortedQueryMetadata.slice(
      pagination.pageIndex * pagination.pageSize,
      (pagination.pageIndex + 1) * pagination.pageSize,
    );
  }, [sortedQueryMetadata, pagination]);

  useEffect(() => {
    if (sortedQueryMetadata.length > 0) {
      setSelection({ [sortedQueryMetadata[0].id]: true });
    }
  }, [sortedQueryMetadata]);

  const columnHelperWrapper = createColumnHelper<QueryMetadata>();

  const columns = useMemo(
    () => [
      columnHelperWrapper.accessor({
        accessorKey: () => "select",
        columnDef: {
          id: "select",
          cell: (cell) => {
            return (
              <div
                className="h-full w-full flex-center cursor-pointer pl-2"
                onClick={cell.row.getToggleSelectedHandler()}
              >
                <Radio
                  label=""
                  value=""
                  checked={cell.row.getIsSelected()}
                  onChange={() => null}
                />
              </div>
            );
          },
          size: 40,
          maxSize: 40,
          header: "",
        },
      }),
      columnHelperWrapper.accessor({
        accessorKey: (r) => `${r.context} Page`,
        columnDef: {
          id: "context",
          cell: (cell) => <CellTextFormatter className="pl-0" cell={cell} />,
          header: (header) => (
            <HeaderWithInfoIcon
              className="pl-0"
              title="Context"
              header={header}
            />
          ),
          enableSorting: true,
        },
      }),
      columnHelperWrapper.accessor({
        accessorKey: (r) => r.topic,
        columnDef: {
          id: "topic",
          cell: (cell) => <CellTextFormatter cell={cell} />,
          header: (header) => (
            <HeaderWithInfoIcon title="Topic" header={header} />
          ),
          enableSorting: true,
        },
      }),
      columnHelperWrapper.accessor({
        accessorKey: (r) => r.log.executionTimestamp,
        columnDef: {
          id: "execution-timestamp",
          cell: (cell) => {
            const value = cell.getValue();
            return (
              <CellTextFormatter
                className="font-mono"
                cell={cell}
                value={moment
                  .tz(value, moment.tz.guess())
                  .format("HH:mm:ss.SSS")}
              />
            );
          },
          header: (header) => (
            <HeaderWithInfoIcon title="Timestamp" header={header} />
          ),
          enableSorting: true,
        },
      }),
      columnHelperWrapper.accessor({
        accessorKey: (r) => r.log.jobId,
        columnDef: {
          id: "job-id",
          cell: (cell) => (
            <CellTextFormatter className="font-mono" cell={cell} />
          ),
          header: (header) => (
            <HeaderWithInfoIcon title="Job ID" header={header} />
          ),
          enableSorting: true,
        },
      }),
      columnHelperWrapper.accessor({
        accessorKey: (r) => r.log.queryId,
        columnDef: {
          id: "query-id",
          cell: (cell) => (
            <CellTextFormatter
              className="font-mono"
              cell={cell}
              value={cell.getValue() ?? "-"}
            />
          ),
          header: (header) => (
            <HeaderWithInfoIcon title="Query ID" header={header} />
          ),
          enableSorting: true,
        },
      }),
    ],
    [columnHelperWrapper],
  );

  return (
    <ModalPanel className="flex flex-col overflow-hidden gap-6 h-full pb-2">
      <div className="px-0.5">
        <div className="font-semibold text-base mb-2">Big Query Logs</div>
        <div className="flex gap-2">
          <div
            className={clsx(
              "flex-1 text-xs m-0 bg-v2-sun/300 border border-v2-sun/400 rounded px-3 flex-col",
              "flex  justify-between ",
            )}
          >
            <p className="flex items-center m-0 mt-2">
              WARNING: <strong className="mx-1">Turn caching off</strong> to
              fully utilise this logging system
            </p>
            {updateFeatureFlagLoading && (
              <div className="w-10 my-2">
                <Spinner />
              </div>
            )}
            {!updateFeatureFlagLoading && (
              <Switch
                checked={hasFeatureFlag("doNotCacheNBApi")}
                label={`Caching is ${
                  hasFeatureFlag("doNotCacheNBApi") ? "off" : "on"
                }`}
                onChange={async (event) => {
                  updateFeatureFlag(
                    "doNotCacheNBApi",
                    event.currentTarget.checked,
                  );
                }}
                size="xs"
                className="mt-2"
              />
            )}
          </div>
          <GenericTooltip content="Clear logs" noInfoCircle>
            <SecondaryIconButton
              onClick={() => {
                setQueryMetadata([]);
              }}
            >
              <i className="fa-regular fa-trash text-v2-mars/400"></i>
            </SecondaryIconButton>
          </GenericTooltip>
        </div>
      </div>
      <div className="flex-1 overflow-hidden flex gap-2">
        <div className="flex-1 text-xs overflow-hidden flex flex-col gap-3">
          <div className="flex gap-2">
            <div>
              <div className="text-xs font-semibold mb-1">Context</div>
              <Select
                buttonClassName="w-48"
                clearable
                options={uniq(queryMetadata.map((e) => e.context)).map((e) => ({
                  value: e,
                  label: e,
                }))}
                value={selectedContext}
                onChange={(val) => setSelectedContext(val)}
              />
            </div>
            <div>
              <div className="text-xs font-semibold mb-1">Topic</div>
              <TextInput
                className="w-48"
                placeholder="Filter..."
                value={selectedTopic}
                onChange={(e) => {
                  setSelectedTopic(e.target.value);
                }}
              />
            </div>
          </div>
          <Table
            containerClassName="flex-1 flex flex-col overflow-hidden"
            className="flex-1 overflow-auto"
            columns={columns}
            rows={pagedQueryMetadata}
            getRowId={(row) => row.id}
            noDataContent={
              <div
                className={clsx(
                  "text-v2-primary text-sm font-semibold tracking-wide flex flex-col items-center",
                )}
              >
                No data available
              </div>
            }
            rowCount={filteredQueryMetadata.length}
            multiSort={false}
            shouldHighlightOnHover
            columnWidths={columnWidths}
            onColumnWidthsChange={setColumnWidths}
            loading={false}
            rowSelection={selection}
            onRowSelectionChange={(selectedIds) => {
              const result: Record<string, boolean> = {};

              for (const id of difference(
                selectedIds,
                Object.keys(selection),
              )) {
                result[id] = true;
              }

              setSelection(result);
            }}
            pagination={pagination}
            onPaginationChange={(val) => {
              setPagination(val);
            }}
            sortingState={sorting}
            onSortingChange={(sorting) => {
              setSorting(sorting);
            }}
          />
        </div>
        <div className="flex-1 flex flex-col overflow-hidden mb-4">
          <Panel
            className="flex-1 m-0 p-0 w-auto"
            row={
              selectedRow
                ? {
                    bqLog: selectedRow.log,
                    frontendContext: {
                      context: selectedRow.context,
                      topic: selectedRow.topic,
                    },
                  }
                : null
            }
          />
        </div>
      </div>
    </ModalPanel>
  );
};
